import { Injectable } from '@angular/core';
import {
  Directory,
  FileInfo,
  Filesystem,
  GetUriResult,
  ReadFileResult,
} from '@capacitor/filesystem';
import { FileOpener } from '@capacitor-community/file-opener';
import { Browser } from '@capacitor/browser';
import { BehaviorSubject } from 'rxjs';
import { Capacitor } from '@capacitor/core';

import { IntermediaryService } from '#services/intermediary/intermediary.service';
import { logger } from '#lib/logger';
import { BucketName } from '#models/db/asset/asset.model';

/** How long a download's blob URL is kept alive before it is released. */
const BLOB_REVOKE_DELAY_MS = 10000;

@Injectable({
  providedIn: 'root',
})
export class FileSystemService {
  openFileLoading$ = new BehaviorSubject<boolean>(false);

  private _directory = Directory.Data;

  constructor(private intermediaryHandler: IntermediaryService) {}

  /**
   * Checks if a file exists at the specified path within the configured directory.
   *
   * @param path - The relative path to the file to check.
   * @returns A promise that resolves to the file's information (`FileInfo`) if it exists, or `null` if it does not.
   */
  async fileExists(path: string): Promise<FileInfo | null> {
    try {
      const response: FileInfo = await Filesystem.stat({
        path,
        directory: this._directory,
      });

      return response;
    } catch (error: unknown) {
      logger.log(error, 'FileSystemService.checkFileExists -> File does not exist');

      return null;
    }
  }

  /**
   * Reads the contents of a file at the specified path within the configured directory.
   *
   * @param path - The relative path to the file to be read.
   * @returns A promise that resolves to a `ReadFileResult` containing the file's contents if successful,
   *          or `null` if the file does not exist or an error occurs during reading.
   */
  async readFile(path: string): Promise<ReadFileResult | null> {
    try {
      const result: ReadFileResult = await Filesystem.readFile({
        path,
        directory: this._directory,
      });

      logger.log(path, 'FileSystemService.readFile -> File read successfully.');

      return result;
    } catch (_e: unknown) {
      logger.log(path, 'FileSystemService.readFile -> Error reading file or file does not exist:');

      return null;
    }
  }

  /**
   * Reads a file from the given URI and returns its contents as a Blob.
   *
   * Converts the provided file URI to a web-accessible path using Capacitor,
   * fetches the file, and returns the resulting Blob object.
   * If an error occurs during the process, logs the error and returns `null`.
   *
   * @param fileUri - The URI of the file to read.
   * @returns A Promise that resolves to a Blob containing the file's data, or `null` if an error occurs.
   */
  async readFileAsBlob(fileUri: string): Promise<Blob | null> {
    try {
      const webPath = Capacitor.convertFileSrc(fileUri);
      const response = await fetch(webPath);
      const blob = await response.blob();

      return blob;
    } catch (error) {
      logger.log(error, 'FileSystemService.readFileAsBlob -> Error reading file as blob:');

      return null;
    }
  }

  /**
   * Retrieves the file URI for a given path in the specified directory.
   *
   * @param path - The relative path to the file whose URI is to be retrieved.
   */
  async getFileUri(path: string): Promise<string | null> {
    const result: GetUriResult = await Filesystem.getUri({
      path,
      directory: this._directory,
    });

    return result ? result.uri : null;
  }

  /**
   * Opens a file from a given signed URL using the device's native file opener.
   *
   * Sets the following:
   * * {@link openFileLoading$} - A loading state that indicates whether the file is being opened.
   *
   * This method performs the following steps:
   * 1. Checks and requests necessary permissions on Android devices to access public storage.
   * 2. Checks if the file already exists in the DATA directory. {@link Directory.Data}
   * 3. Writes the file to the device's file system if it does not exist.
   * 4. Opens the file using the default application associated with its type.
   * 5. Handles errors and updates the loading state accordingly.
   *
   * @param signedUrl - The signed URL of the file to be opened.
   * @param path - Include the bucket name and path where the file should be saved or checked. E.g., `bucket-name/path/to/file.ext`
   * @param silent - If true, suppresses loading indicators during the operation. Defaults to true.
   * @returns A promise that resolves when the file has been opened, or rejects if an error occurs.
   * @throws Will throw an error if the required permissions are not granted or if any file operation fails.
   */
  async openFile(signedUrl: string, path: string, silent = true): Promise<void> {
    this.intermediaryHandler.intermediary(
      {
        loading: !silent ? this.openFileLoading$ : undefined,
      },
      async () => {
        let filePath: string;

        try {
          // Check if it exists, use its uri.
          const stat: FileInfo = await Filesystem.stat({
            path,
            directory: this._directory,
          });

          filePath = stat.uri;

          logger.log('File exists, using existing file URI:', filePath);
        } catch (_e: unknown) {
          // If stat fails, file does not exist, so write it.
          filePath = await this.saveFile(signedUrl, path);
        }

        filePath = this.removeTrailingSlashFromUri(filePath);

        // Dismiss loading before opening the native viewer, since FileOpener.open()
        // resolves only when the viewer closes — not when it opens.
        this.openFileLoading$.next(false);

        logger.log('Opening file with URI:', filePath);

        await FileOpener.open({
          filePath,
          openWithDefault: true,
        });
      }
    );
  }

  /**
   * Saves a file from a given signed URL to the specified path.
   *
   * This method retrieves the file as a Base64-encoded string from the provided signed URL,
   * writes it to the given path using a file writer handler, and logs the operation.
   *
   * @param signedUrl - The signed URL from which to fetch the file.
   * @param path - The path where the file should be saved, including the bucket name and file name. E.g., `bucket-name/path/to/file.ext`
   * @returns A promise that resolves to the file path where the file was saved.
   */
  async saveFile(signedUrl: string, path: string): Promise<string> {
    const dataUrl = await this.getFileAsBase64(signedUrl);
    const filePath = await this.fileWriterHandler(dataUrl, path);

    logger.log(path, 'FileSystemService.saveFile -> File saved successfully.');

    return filePath;
  }

  /**
   * Removes a file from the native file system.
   *
   * This method checks for the necessary permissions on Android devices before attempting to delete the file.
   * If the required public storage permission is not granted, it throws an error.
   * Attempts to delete the specified file from the configured directory. If the file does not exist or cannot be deleted,
   * it logs an appropriate message.
   *
   * @param path - Include the bucket name and path where the file is located. E.g., `bucket-name/path/to/file.ext`
   * @returns A promise that resolves when the operation is complete.
   * @throws {Error} If permission to access public storage is not granted on Android.
   */
  async removeFile(path: string): Promise<void> {
    const filePath = this.removeTrailingSlashFromUri(path);

    try {
      await Filesystem.deleteFile({
        path: filePath,
        directory: this._directory,
      });

      logger.log(path, 'FileSystemService.removeFile -> File deleted successfully.');
    } catch (_e: unknown) {
      logger.log(
        path,
        'FileSystemService.removeFile -> File does not exist or could not be deleted.'
      );
    }
  }

  /**
   * Removes a directory at the specified path. Attempts to delete the directory and all its contents recursively.
   *
   * @param directoryPath - Include the bucket name and path where the directory is located. E.g., `bucket-name/path/to/directory`
   * @returns A promise that resolves when the operation is complete.
   *
   * @remarks
   * Make sure to include the user ID with the directory path, as this service is designed to work with user-specific directories.
   */
  async removeDirectory(directoryPath: string): Promise<void> {
    const path = this.removeTrailingSlashFromUri(directoryPath);

    try {
      await Filesystem.rmdir({
        path: path,
        directory: this._directory,
        recursive: true,
      });

      logger.log(
        directoryPath,
        'FileSystemService.removeDirectory -> Directory deleted successfully.'
      );
    } catch (_e) {
      logger.log(
        directoryPath,
        'FileSystemService.removeDirectory -> Directory does not exist or could not be deleted.'
      );
    }
  }

  /**
   * Reads the contents of a directory at the specified path.
   *
   * @param path - The relative path to the directory to read.
   * @returns A promise that resolves to an array of `FileInfo` objects representing the files in the directory.
   *          If an error occurs, the promise resolves to an empty array.
   */
  async readDirectory(path: string): Promise<FileInfo[]> {
    try {
      const result = await Filesystem.readdir({
        path,
        directory: this._directory,
      });

      logger.log(
        result.files ?? path,
        'FileSystemService.readDirectory -> Directory read successfully.'
      );

      return result.files;
    } catch (_e: unknown) {
      logger.log(path, 'FileSystemService.readDirectory -> Error reading directory:');

      return [];
    }
  }

  /**
   * Opens a signed URL in a new browser tab for viewing.
   *
   * Sets the following:
   * * {@link openFileLoading$} - A loading state that indicates whether the file is being opened.
   *
   * @param signedUrl - The signed URL of the file to open.
   * @param silent - If true, suppresses loading indicators during the operation. Defaults to true.
   * @returns A promise that resolves when the browser has attempted to open the URL.
   *
   * @remarks
   * Only used for web platforms, as it utilizes the `Browser` API to open the URL.
   *
   * The signed URL is opened directly instead of being fetched into a blob URL. On web,
   * `Browser.open()` is just `window.open()` and returns immediately, so a blob URL created
   * here is revoked before the new tab can re-read it. The image still paints from the
   * in-flight navigation, but the browser's "Save as" re-requests the dead `blob:` URL and
   * fails. Signed URLs stay valid for 24 hours, so the new tab can re-fetch on demand and
   * gets a real filename from the response instead of a blob UUID.
   */
  async openUrl(signedUrl: string, silent = true): Promise<void> {
    this.intermediaryHandler.intermediary(
      {
        loading: !silent ? this.openFileLoading$ : undefined,
      },
      async () => {
        // Dismiss loading before opening the browser, since Browser.open()
        // resolves only when the browser closes — not when it opens.
        this.openFileLoading$.next(false);

        await Browser.open({ url: signedUrl });
      }
    );
  }

  /**
   * Downloads a file from a signed URL directly to the user's device.
   *
   * Sets the following:
   * * {@link openFileLoading$} - A loading state that indicates whether the file is being downloaded.
   *
   * @param signedUrl - The signed URL of the file to download.
   * @param path - Include the bucket name and path the file was stored under. E.g., `bucket-name/path/to/file.ext`
   * @param silent - If true, suppresses loading indicators during the operation. Defaults to true.
   * @returns A promise that resolves once the download has been handed to the browser.
   *
   * @remarks
   * Only used for web platforms. This is the counterpart to {@link openFile}, which opens the
   * native viewer, and takes the same `path` so the two branches stay symmetrical.
   *
   * The file is fetched into a blob URL because the `download` attribute is ignored for
   * cross-origin URLs, and storage is served from a different origin. Downloading rather than
   * opening a tab also keeps the signed URL, and its 24 hour token, out of the address bar
   * and browser history.
   */
  async downloadFile(signedUrl: string, path: string, silent = true): Promise<void> {
    this.intermediaryHandler.intermediary(
      {
        loading: !silent ? this.openFileLoading$ : undefined,
      },
      async () => {
        const response = await fetch(signedUrl);
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);

        const anchor = document.createElement('a');

        anchor.href = url;
        anchor.download = this.fileNameFromPath(path);
        anchor.rel = 'noopener';

        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();

        this.openFileLoading$.next(false);

        // The browser captures the blob when the click is dispatched, so revoking on a
        // later task is safe and releases the buffer.
        setTimeout(() => URL.revokeObjectURL(url), BLOB_REVOKE_DELAY_MS);

        logger.log(path, 'FileSystemService.downloadFile -> Download started.');
      }
    );
  }

  /**
   * Extracts the file name from a storage path.
   *
   * @param path - The storage path, e.g. `bucket-name/user-id/document-id/my-will_123.pdf`.
   * @returns The trailing file name, or an empty string when the path has no segments.
   *
   * @remarks
   * Uploads store the sanitized original file name as the last path segment
   * (see `FilePickerService.addFileToProcessQueue`), so this recovers a readable name.
   */
  private fileNameFromPath(path: string): string {
    return path.split('/').filter(Boolean).pop() ?? '';
  }

  /**
   * Writes a file to the DATA directory using the provided data URL and file name.
   *
   * @param dataUrl - The data to be written to the file, encoded as a data URL.
   * @param path - The name of the file to be created in the DATA directory.
   * @returns A promise that resolves to a `WriteFileResult` containing information about the written file.
   * @throws Will throw an error if the file could not be written to the file system.
   */
  private async fileWriterHandler(dataUrl: string, path: string): Promise<string> {
    const result = await Filesystem.writeFile({
      path,
      data: dataUrl,
      directory: this._directory,
      recursive: true,
    });

    if (!result) {
      throw new Error('Failed to write file to the file system.');
    }

    let filePath = result.uri;

    filePath = this.removeTrailingSlashFromUri(filePath);

    return filePath;
  }

  /**
   * Asynchronously fetches a file from the given path and returns its contents as a Base64-encoded data URL string.
   *
   * @param path - The URL or path to the file to be fetched.
   * @returns A promise that resolves to a Base64-encoded data URL string representing the file's contents.
   *
   * @example
   * ```typescript
   * const base64String = await getFileAsBase64('/path/to/image.png');
   * // base64String: "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAA..."
   * ```
   */
  private async getFileAsBase64(path: string): Promise<string> {
    const response = await fetch(path);
    const mimeType = response.headers.get('Content-Type');
    const arrayBuffer = await response.arrayBuffer();

    // Convert array buffer to base64 string.
    const uint8Array = new Uint8Array(arrayBuffer);
    let binary = '';

    for (let i = 0; i < uint8Array.byteLength; i++) {
      binary += String.fromCharCode(uint8Array[i]);
    }

    // Encode the binary string to base64.
    const base64 = btoa(binary);

    return `data:${mimeType};base64,${base64}`;
  }

  /**
   * Removes the trailing slash from a file URI if it exists.
   *
   * @param uri The file URI to process.
   * @remarks
   * The trailing slash in the returned file URI (e.g., .../Documents/1751314196208.jpg/) is unusual and not standard for file paths—normally, a trailing slash indicates a directory, not a file.
   * {@link https://github.com/ionic-team/capacitor-filesystem/issues/17}
   */
  removeTrailingSlashFromUri(uri: string): string {
    if (uri.endsWith('/')) {
      return uri.slice(0, -1);
    }

    return uri;
  }
  /**
   * Cleans up the storage by removing any files or directories that are no longer valid.
   * This helps to ensure that the storage remains organized and free of unnecessary files.
   *
   * The code now checks each directory in the user's specified bucket folder: if the directory's
   * name does not match any id in the validIds array, it removes that directory or file. This keeps the
   * file system in sync with the actual data.
   *
   * @param id - Optional ID to specify a subdirectory within the user's bucket folder to clean up.
   * @param validIds - An array of valid IDs (file names or directory names) that should be retained.
   * @param type - The type of items to clean up: 'file' or 'directory'.
   * @param bucketName - The name of the bucket where the files or directories are stored.
   * @param userId - The ID of the user whose storage is being cleaned up.
   */
  async cleanupStorage({
    id,
    validIds,
    type,
    bucketName,
    userId,
  }: {
    id?: string;
    validIds: string[];
    type: 'file' | 'directory';
    bucketName: BucketName;
    userId: string;
  }): Promise<void> {
    const path = id ? `${bucketName}/${userId}/${id}` : `${bucketName}/${userId}`;
    const files = await this.readDirectory(path);

    if (files && files.length) {
      for (const file of files) {
        if (file.type === type && !validIds.includes(file.name)) {
          if (type === 'directory') {
            await this.removeDirectory(`${path}/${file.name}`);
          } else if (type === 'file') {
            await this.removeFile(`${path}/${file.name}`);
          }
        }
      }
    }
  }

  setOpenFileLoading(isLoading: boolean) {
    this.openFileLoading$.next(isLoading);
  }
}
